import { z } from 'zod';

/**
 * 시딩 캠페인(제안형) 상세 화면 상태 — 7종.
 *
 * features-layer 의 `SeedingProposeStatus` 7종 (`pending` … `soldOut`) 과 1:1 대응.
 * BE 는 raw 상태값만 응답하고, FE 가 `participation.status` + `participation.optionsSelected`
 * + `fulfillments[].status` + `contentSubmissions` + `goods[].soldOut` 조합으로
 * 화면을 분기한다 (utils.ts `determineSeedingProposalScreenState`).
 */
export const SEEDING_PROPOSAL_SCREEN_STATE = {
  /** 수락 전·옵션 미선택 — PENDING + optionsSelected = false */
  PENDING: 'PENDING',
  /** 수락 전·옵션 선택 완료 — PENDING + optionsSelected = true */
  PRODUCT_SELECTED: 'PRODUCT_SELECTED',
  /** 수락 후 상품 준비중 — CONFIRMED + 전체 SHIPPING_READY */
  PREPARING: 'PREPARING',
  /** 배송 중 — CONFIRMED + 가장 낮은 단계 = SHIPPING */
  SHIPPING: 'SHIPPING',
  /** 배송 완료 — CONFIRMED + 전체 DELIVERED + contentSubmissions empty */
  DELIVERED: 'DELIVERED',
  /** 콘텐츠 등록 완료 — COMPLETED + 전체 DELIVERED + contentSubmissions not empty */
  CONTENT_SUBMITTED: 'CONTENT_SUBMITTED',
  /** 수락 전 단독 상품 품절·진행 불가 — PENDING + 전체 goods 품절 */
  SOLD_OUT_UNAVAILABLE: 'SOLD_OUT_UNAVAILABLE',
  /** 제안 거절(종료) — REJECTED. 읽기전용 결과 화면(수락/거절 CTA·D-day 미노출). (SID-4210) */
  REJECTED: 'REJECTED',
} as const;

export const SeedingProposalScreenStateSchema = z
  .enum([
    SEEDING_PROPOSAL_SCREEN_STATE.PENDING,
    SEEDING_PROPOSAL_SCREEN_STATE.PRODUCT_SELECTED,
    SEEDING_PROPOSAL_SCREEN_STATE.PREPARING,
    SEEDING_PROPOSAL_SCREEN_STATE.SHIPPING,
    SEEDING_PROPOSAL_SCREEN_STATE.DELIVERED,
    SEEDING_PROPOSAL_SCREEN_STATE.CONTENT_SUBMITTED,
    SEEDING_PROPOSAL_SCREEN_STATE.SOLD_OUT_UNAVAILABLE,
    SEEDING_PROPOSAL_SCREEN_STATE.REJECTED,
  ])
  .describe('SeedingProposalScreenState');

export type SeedingProposalScreenState = z.infer<typeof SeedingProposalScreenStateSchema>;

// BE 정식값은 TWENTY_NINE_CM. 29CM 도 수용 후 29CM 로 정규화(상세 응답 platform 은 미소비 — 서비스 분기는 device service 사용).
const PlatformSchema = z
  .enum(['MUSINSA', '29CM', 'TWENTY_NINE_CM'])
  .transform((value) => (value === 'TWENTY_NINE_CM' ? '29CM' : value))
  .describe('SeedingProposalPlatform');

const CampaignStatusSchema = z
  .enum(['ACTIVE', 'INACTIVE'])
  .describe('SeedingProposalCampaignStatus');

const SnsChannelSchema = z
  .enum(['INSTAGRAM', 'YOUTUBE', 'TWITTER', 'TIKTOK', 'NAVER_BLOG', 'THREADS', 'SNAP'])
  .describe('SeedingProposalSnsChannel');

const ContentTypeSchema = z
  .enum(['IMAGE', 'SHORT_FORM', 'LONG_FORM', 'TEXT', 'BLOG_POST'])
  .describe('SeedingProposalContentType');

const ContentTypeOptionSchema = z
  .object({
    type: ContentTypeSchema,
    uploadDeadlineDays: z.number().int(),
  })
  .describe('SeedingProposalContentTypeOption');

export const SeedingProposalCampaignSchema = z
  .object({
    id: z.number().int(),
    platform: PlatformSchema,
    brandId: z.string(),
    // 표시용 브랜드명(예: '032씨'). brandId 는 코드('032c'). 응답엔 있으나 문서 미선언이라 nullish.
    brandName: z.string().nullish(),
    title: z.string(),
    status: CampaignStatusSchema,
    snsChannels: z.array(SnsChannelSchema),
    contentTypes: z.array(ContentTypeOptionSchema),
    requiredHashtags: z.array(z.string()),
    /** `requiredHashtags`(각 항목에 '#' 포함) 를 공백 join 한 표시·복사용 문자열 (service 가 derive). */
    hashtagText: z.string(),
    requestNote: z.string().nullable(),
    /** 모집형 전용 — 제안형은 null (통일 스키마). 제안형 수락 마감은 participation.responseDeadlineAt 사용. */
    recruitmentEndAt: z.string().nullable(),
  })
  .describe('SeedingProposalCampaign');

export type SeedingProposalCampaign = z.infer<typeof SeedingProposalCampaignSchema>;

export const SeedingProposalGoodsItemSchema = z
  .object({
    id: z.number().int(),
    goodsNo: z.number().int(),
    slotNo: z.number().int(),
    category: z.string(),
    brandName: z.string(),
    goodsName: z.string(),
    imageUrl: z.string(),
    // 상품 PDP 랜딩 URL — 썸네일 클릭 시 이동 (완성된 절대 URL). (QAT-39446)
    webUrl: z.string(),
    price: z.number().int(),
    soldOut: z.boolean(),
    // 큐레이터가 이 goods 를 선택했는지(옵션 없는 단품도 선택 시 true). 신청/수락 전이면 false. (QAT-39515)
    selected: z.boolean(),
    // 신청/수락 시 선택한 옵션 표시 문자열(예: '라이트 베이지 · L'). 미선택·단품이면 null. (QAT-39515)
    selectedOption: z.string().nullish(),
  })
  .describe('SeedingProposalGoodsItem');

export type SeedingProposalGoodsItem = z.infer<typeof SeedingProposalGoodsItemSchema>;

const ParticipationStatusSchema = z
  .enum([
    'PENDING',
    'CONFIRMED',
    'COMPLETED',
    'REJECTED',
    'AUTO_TIMEOUT',
    'SELF_WITHDRAWN',
    'EXPIRED',
  ])
  .describe('SeedingProposalParticipationStatus');

const ParticipationTypeSchema = z.enum(['PROPOSAL']).describe('SeedingProposalParticipationType');

export const SeedingProposalParticipationSchema = z
  .object({
    id: z.number().int(),
    status: ParticipationStatusSchema,
    participationType: ParticipationTypeSchema,
    /** 제안 수락 응답 기한 (제안형 48h, 경과 시 AUTO_TIMEOUT). 모집형은 null. */
    responseDeadlineAt: z.string().nullable(),
    /** 통일 응답(SID-2659)엔 미포함이라 optional. 미제공 시 옵션 선택은 클라이언트 상태로 판단. */
    optionsSelected: z.boolean().optional(),
    selectedContentType: ContentTypeSchema.nullable(),
    messageToBrand: z.string().nullable(),
    contentSubmissionDeadlineAt: z.string().nullable(),
    confirmedAt: z.string().nullable(),
    endedAt: z.string().nullable(),
  })
  .describe('SeedingProposalParticipation');

export type SeedingProposalParticipation = z.infer<typeof SeedingProposalParticipationSchema>;

const FulfillmentStatusSchema = z
  .enum(['SHIPPING_READY', 'SHIPPING', 'DELIVERED', 'CANCELLED'])
  .describe('SeedingProposalFulfillmentStatus');

export const SeedingProposalFulfillmentSchema = z
  .object({
    id: z.number().int(),
    orderId: z.string(),
    orderOptionId: z.number().int(),
    status: FulfillmentStatusSchema,
    shippingDeadlineAt: z.string(),
    shippedAt: z.string().nullable(),
    deliveredAt: z.string().nullable(),
    // 택배사명/송장번호/배송추적URL — 송장 등록 시 노출(배송준비중 포함), 미등록·취소·미지원 시 null. (API 0610 SeedingDetailFulfillment)
    courierName: z.string().nullish(),
    invoiceNo: z.string().nullish(),
    trackingUrl: z.string().nullish(),
  })
  .describe('SeedingProposalFulfillment');

export type SeedingProposalFulfillment = z.infer<typeof SeedingProposalFulfillmentSchema>;

export const SeedingProposalContentSubmissionSchema = z
  .object({
    id: z.number().int(),
    channel: SnsChannelSchema,
    contentType: ContentTypeSchema,
    contentUrl: z.string(),
  })
  .describe('SeedingProposalContentSubmissionItem');

export type SeedingProposalContentSubmission = z.infer<
  typeof SeedingProposalContentSubmissionSchema
>;

const DeliveryAddressSourceSchema = z
  .enum(['DEFAULT', 'ORDER_SNAPSHOT'])
  .describe('SeedingProposalDeliveryAddressSource');

export const SeedingProposalDeliveryAddressSchema = z
  .object({
    source: DeliveryAddressSourceSchema,
    // 배송지 별칭('우리집' 등). ORDER_SNAPSHOT 만 보유 / DEFAULT 는 null
    addressName: z.string().nullable(),
    name: z.string(),
    phone: z.string(),
    address1: z.string(),
    address2: z.string().nullable(),
    // 0610 추가 — 배송지 id / 우편번호 / 메모 (null 가능)
    deliveryAddressId: z.number().int().nullable().optional(),
    zipcode: z.string().nullable().optional(),
    memo: z.string().nullable().optional(),
  })
  .describe('SeedingProposalDeliveryAddress');

export type SeedingProposalDeliveryAddress = z.infer<typeof SeedingProposalDeliveryAddressSchema>;

export const SeedingProposalPenaltySchema = z
  .object({
    // 응답에 포함되면 항상 활성 패널티로 간주 (active 플래그 없음)
    reason: z.string(),
  })
  .describe('SeedingProposalPenalty');

export type SeedingProposalPenalty = z.infer<typeof SeedingProposalPenaltySchema>;

export const GetSeedingProposalDetailServiceRequestSchema = z
  .object({
    campaignId: z.number().int(),
  })
  .describe('GetSeedingProposalDetailServiceRequest');

export type GetSeedingProposalDetailServiceRequest = z.infer<
  typeof GetSeedingProposalDetailServiceRequestSchema
>;

export const GetSeedingProposalDetailServiceResponseSchema = z
  .object({
    /** 화면 상태 결정 로직으로 도출한 7종 화면 enum */
    screenState: SeedingProposalScreenStateSchema,
    campaign: SeedingProposalCampaignSchema,
    goods: z.array(SeedingProposalGoodsItemSchema),
    participation: SeedingProposalParticipationSchema,
    fulfillments: z.array(SeedingProposalFulfillmentSchema),
    contentSubmissions: z.array(SeedingProposalContentSubmissionSchema),
    deliveryAddress: SeedingProposalDeliveryAddressSchema.nullable(),
    penalty: SeedingProposalPenaltySchema.nullable(),
    /** 발송 기한 영업일 수 N ("선정일로부터 영업일 N일 이내"). BE 정책 상수. (API 0615) */
    shippingDeadlineDays: z.number().int().nullable(),
    /** 예상 발송 마감일 (KST). 제안형=조회 시점+N영업일. 공휴일 조회 실패 시 null. (API 0615) */
    estimatedShippingDeadlineAt: z.string().nullable(),
  })
  .describe('GetSeedingProposalDetailServiceResponse');

export type GetSeedingProposalDetailServiceResponse = z.infer<
  typeof GetSeedingProposalDetailServiceResponseSchema
>;
